const ChatbotState = {
  Open: 'open',
  Closed: 'closed',
  Minimized: 'minimized'
};

// Renew token 2 minutes before expiry
const TOKEN_SKEW_MS = 2 * 60 * 1000;
const getStorageKey = locale => `pvaDirectLineToken_${locale}`;

let directLineInstance = null;
let directLineConnectionSub = null;
let chatbotInitialized = false;
let isLoadingChatbot = false;

const decodeJwtExpiryMs = token => {
  try {
    const [, payloadB64] = token.split('.');
    const payload = JSON.parse(
      atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return typeof payload.exp === 'number' ? payload.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

const readCachedToken = storageKey => {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedToken = (storageKey, token) => {
  const expiresAt = decodeJwtExpiryMs(token) || Date.now() + 25 * 60 * 1000;
  localStorage.setItem(storageKey, JSON.stringify({ token, expiresAt }));
};

const isTokenValid = expiresAt =>
  typeof expiresAt === 'number' && expiresAt - Date.now() > TOKEN_SKEW_MS;

const refreshDirectLineToken = async (directLineURL, token) => {
  const url = new URL('v3/directline/tokens/refresh', directLineURL);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to refresh Direct Line token.');
  const { token: newToken } = await res.json();
  return newToken;
};

const getOrReuseToken = async (tokenEndpointURL, directLineURL, storageKey) => {
  const cached = readCachedToken(storageKey);
  if (cached && isTokenValid(cached.expiresAt)) {
    return cached.token;
  }
  if (cached && cached.token) {
    try {
      const refreshed = await refreshDirectLineToken(
        directLineURL,
        cached.token
      );
      writeCachedToken(storageKey, refreshed);
      return refreshed;
    } catch (err) {
      if (console?.warn) {
        console.warn(
          'Direct Line token refresh failed, requesting a new token.',
          err
        );
      }
    }
  }
  const res = await fetch(tokenEndpointURL);
  if (!res.ok) throw new Error('Failed to retrieve Direct Line token.');
  const { token } = await res.json();
  writeCachedToken(storageKey, token);
  return token;
};

const showChatbot = (iframeContainer, openBtn) => {
  iframeContainer.style.display = 'flex';
  openBtn.style.display = 'none';
};

const hideChatbot = (iframeContainer, openBtn) => {
  iframeContainer.style.display = 'none';
  openBtn.style.display = 'none';
};

const minimizeChatbot = (iframeContainer, openBtn) => {
  iframeContainer.style.display = 'none';
  openBtn.style.display = 'flex';
};

const loadChatbot = async () => {
  if (directLineInstance || isLoadingChatbot) return;
  isLoadingChatbot = true;

  try {
    const styleOptions = {
      accent: '#00809d',
      botAvatarBackgroundColor: '#FFFFFF',
      botAvatarInitials: '',
      userAvatarInitials: '',
      hideUploadButton: true
    };

    const norTokenEndpointURL = new URL(
      'https://5333f9735c914705936c651ffeb94a.0e.environment.api.powerplatform.com/powervirtualagents/botsbyschema/crf5c_nrfCopilotNo/directline/token?api-version=2022-03-01-preview'
    );
    const engTokenEndpointURL = new URL(
      'https://5333f9735c914705936c651ffeb94a.0e.environment.api.powerplatform.com/powervirtualagents/botsbyschema/crf5c_nrfCopilotEn/directline/token?api-version=2022-03-01-preview'
    );

    const locale = document.documentElement.lang || 'en';
    const tokenEndpointURL =
      locale === 'no' ? norTokenEndpointURL : engTokenEndpointURL;
    const apiVersion = tokenEndpointURL.searchParams.get('api-version');

    const directLineURL = await fetch(
      new URL(
        `/powervirtualagents/regionalchannelsettings?api-version=${apiVersion}`,
        tokenEndpointURL
      )
    )
      .then(response => {
        if (!response.ok)
          throw new Error('Failed to retrieve regional channel settings.');
        return response.json();
      })
      .then(({ channelUrlsById: { directline } }) => directline);

    const storageKey = getStorageKey(locale);
    const token = await getOrReuseToken(
      tokenEndpointURL,
      directLineURL,
      storageKey
    );

    // eslint-disable-next-line no-undef
    const directLine = WebChat.createDirectLine({
      domain: new URL('v3/directline', directLineURL),
      token
    });
    directLineInstance = directLine;

    directLineConnectionSub = directLine.connectionStatus$.subscribe({
      next(value) {
        if (value === 2) {
          directLine
            .postActivity({
              localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              locale,
              name: 'startConversation',
              type: 'event'
            })
            .subscribe();
        }
        if (value === 3) {
          localStorage.removeItem(storageKey);
        }
      }
    });

    // eslint-disable-next-line no-undef
    WebChat.renderWebChat(
      { directLine, locale, styleOptions },
      document.getElementById('webchat')
    );

    const textInput = document.querySelector(
      'input.webchat__send-box-text-box__input'
    );
    const openBtn = document.querySelector('#openBtn');
    if (openBtn) {
      const inputPlaceholderText = openBtn.getAttribute('data-input-text');
      openBtn.removeAttribute('data-input-text');
      if (textInput && inputPlaceholderText) {
        textInput.placeholder = inputPlaceholderText;
      }
    }
  } catch (err) {
    console.error('Chatbot load failed', err);
  } finally {
    isLoadingChatbot = false;
  }
};

const destroyChatbotSession = ({ clearToken = false } = {}) => {
  if (directLineConnectionSub) {
    try {
      directLineConnectionSub.unsubscribe();
    } catch (err) {
      console.error(
        'Chatbot - Error unsubscribing from Direct Line connection',
        err
      );
    }
    directLineConnectionSub = null;
  }
  if (directLineInstance && typeof directLineInstance.end === 'function') {
    try {
      directLineInstance.end();
    } catch (err) {
      console.error('Chatbot - Error ending Direct Line connection', err);
    }
  }
  directLineInstance = null;

  const webchatEl = document.getElementById('webchat');
  if (webchatEl) webchatEl.innerHTML = '';

  if (clearToken) {
    const locale = document.documentElement.lang || 'en';
    localStorage.removeItem(getStorageKey(locale));
  }
};

// Always start a new session (destroy then load)
const startNewSession = () => {
  destroyChatbotSession();
  loadChatbot();
};

window.destroyChatbotSession = destroyChatbotSession;
window.startNewSession = startNewSession;

const init = () => {
  const openBtn = document.getElementById('openBtn');
  if (!openBtn) return;

  const closeBtn = document.getElementById('closeBtn');
  const minimizeBtn = document.getElementById('minimizeBtn');
  const iframeContainer = document.getElementById('iframeContainer');

  // Dynamically create footer buttons and bind NEW session behavior
  window.addEventListener('load', () => {
    const containerList = document.querySelectorAll(
      '.chatbot-button-container'
    );
    if (containerList.length === 0) return;

    const openBtnElement = document.getElementById('openBtn');
    if (!openBtnElement) return;

    const chatbotText = openBtnElement.innerHTML;

    containerList.forEach(container => {
      if (container.dataset.bound === '1') return;
      container.dataset.bound = '1';

      const button = document.createElement('button');
      button.className = container.classList.contains('chatbot-button-dark')
        ? 'button openChatbotFromFooterBtn -theme-primary -theme-large -theme-dark -has-children'
        : 'button openChatbotFromFooterBtn -theme-primary -theme-large -theme-light -has-children';
      button.textContent = chatbotText;
      container.appendChild(button);

      button.addEventListener('click', () => {
        // Footer button always opens a NEW session
        destroyChatbotSession();
        showChatbot(iframeContainer, openBtn);
        localStorage.setItem('chatbotState', ChatbotState.Open);
        loadChatbot(); // This is opening a new instance
      });
    });
  });

  let chatbotState = localStorage.getItem('chatbotState');
  if (!chatbotState) {
    chatbotState = ChatbotState.Minimized;
    localStorage.setItem('chatbotState', chatbotState);
  }

  openBtn.addEventListener('click', () => {
    showChatbot(iframeContainer, openBtn);
    localStorage.setItem('chatbotState', ChatbotState.Open);
    if (!directLineInstance) loadChatbot();
  });

  closeBtn.addEventListener('click', () => {
    hideChatbot(iframeContainer, openBtn);
    localStorage.setItem('chatbotState', ChatbotState.Closed);
    destroyChatbotSession();
  });

  minimizeBtn.addEventListener('click', () => {
    minimizeChatbot(iframeContainer, openBtn);
    localStorage.setItem('chatbotState', ChatbotState.Minimized);
  });

  if (chatbotInitialized) return;

  switch (chatbotState) {
    case ChatbotState.Open:
      showChatbot(iframeContainer, openBtn);
      loadChatbot();
      break;
    case ChatbotState.Closed:
      hideChatbot(iframeContainer, openBtn);
      break;
    case ChatbotState.Minimized:
    default:
      minimizeChatbot(iframeContainer, openBtn);
      break;
  }

  chatbotInitialized = true;
};

init();
